/**
 * Defines basic type for all entities.
 */
import { Primitive } from '@geniuspos/ui-shared';

import { IAction, IApiListResponse, IFilterQuery } from '../../common';
import { EntityId, ObjectId } from '../entity-id.type';
import {
  ACTION_STATUS,
  FieldsToKeepInDocumentAfterUpdateEnum,
} from '@config/index';
import { EHeaderName, ENTITY_TYPES } from '../../enums';
import { AbstractControl, AsyncValidatorFn, FormGroup } from '@angular/forms';
import { XVIError } from '@app/shared/models';
import { Observable } from '@shared/rx';
import { Injector } from '@angular/core';

// eslint-disable-next-line max-len
export interface IBaseEntityType {
  _id?: ObjectId;
  is_active?: boolean;
  company_id?: ObjectId;
  entity_id?: EntityId;
  is_master?: boolean;
  external_id?: string;
  created_by?: ObjectId;
  updated_by?: ObjectId;
  created_at?: string;
  updated_at?: string;
  name?: string;
  virtual?: {
    [key: string]: any;
  };

  // this allows us to assign an instance of IBaseEntityType to any other EntityType, like product
  [key: string]: any;
}

export interface IDocumentOperationOptions {
  newLocation?: string | boolean;
  skipSchema?: boolean;
  noLoader?: boolean;
  keepUntilFetch?: boolean;
  fetchList?: boolean;
  query?: IFilterQuery;
  requestId?: string;
  skipMappers?: boolean;
  skipPickingRequiredFields?: boolean;
  skipUpdateDocument?: boolean;
  suppressErrors?: boolean;
  suppressSuccessMessage?: boolean;
  endpoint?: string;
  mode?: string;
  requiredToSendFields?: string[];
  continueFetchCondition?: Function;
  // if you want to custom message we should suppressErrors =true and config yourself messages
  customErrorsFn?: Function;

  /**
   * additional fetch flag arr
   * your can put your addition fetch flag here to judge is need to fetch additional data
   */
  additionalFetch?: string[];

  /**
   * Flag is saying that for fetching list of items would be
   * fetched all data page-by-page if the top is less than amount of data
   * Example:
   * {
   *    total: 20000,
   *    items: [...],
   *    page_site: 10000,
   * }
   *
   * There will be two requests to retrieve
   * all 20000 data with parameters `top: 10000, skip: 10000`
   */
  fetchFullListData?: boolean;

  /**
   * Flag used for suppress TimeoutError during request
   * Error checked using isLambdaTimeoutError function
   */
  suppressTimeoutError?: boolean;

  /**
   * Used for sending headers parameters with requests to API
   * 'x-moms-name' is used on API to apply moms package name
   */
  headers?: DocumentOperationHeaders;

  /**
   * Used to restrict sending "BUSINESS_DATE_HEADERS" headers parameters with requests to API
   */
  disallowBusinessDateHeaders?: boolean;

  /**
   * Flag used for provide information about Delete View Mode
   */
  isDeleteViewMode?: boolean;

  /**
   * Allows to skip the "Save in progress" message
   */
  suppressPendingStatus?: boolean;
  /**
   * This flag informs that data has already prepared and we can send it to the server as it is
   */
  dataIsPrepared?: boolean;

  /**
   * use this to override request error message
   */
  errorOverride?: IErrorOverride;

  /**
   * need to submit after successful saving
   */
  needSubmit?: boolean;

  requestPayload?: any;

  /**
   * Used to determine whether to display a toaster message when an HTTP error occurs.
   */
  showErrorMessage?: boolean;

  /**
   * Used to control is need throw error out to outside when api request
   */
  throwError?: boolean;

  /**
   * Used to disable permission check when api request
   */
  notCheckPermissionError?: boolean;

  /**
   * use this to show notification popup when operation success
   */
  successNotificationMessage?: {
    title?: string;
    message?: string;
  };

  skipValidate?: boolean;
  include_inactive?: boolean;
  include_entities?: ENTITY_TYPES[];

  userDefinedLocales?: string;

  /**
   * Flag which will be transformed into the isDocumentForEditor for applyUserDefinedTranslations
   * currently only /current endpoint supports this flag
   */
  isDataForEditor?: boolean;

  transactionId?: ObjectId;

  /**
   * Key-value pairs of dependent entities data that are fetched alongside the main entity list.
   * The keys are defined by the service's dependent request params, and the values are the corresponding API responses.
   * Example:
   * {
   *   usersInfo: { items: [...], total: 100, ... },
   *   productsInfo: { items: [...], total: 50, ... },
   * }
   */
  dependentEntitiesData?: Partial<
    Record<ENTITY_TYPES | string, IApiListResponse<IBaseEntityType> | null>
  >;

  /**
   * This is used for some special case when we want to use a defined fetch list method which is different from default one in service
   */
  customizeFetchListMethod?: string;

  /**
   * use this to configure the form value getter,
   * default is form.value
   */
  formValueGetter?: <V = AbstractControl['value']>(getterParams: {
    form: FormGroup;
    documentItem: IBaseEntityType;
    params?: IDocumentOperationOptions;
  }) => V;
}

export interface IGridOptions {
  gridLoader?: boolean;
}

export interface IErrorOverride {
  /**
   * common error, will use this to replace default common error message
   */
  commonError?: string;

  /**
   * api error, will use this to replace api error message
   */
  apiError?: string;
}

export type DocumentOperationHeaders = {
  [key in EHeaderName]?: string;
};

export interface IRequestPayload {
  options?: IDocumentOperationOptions;
  query?: IFilterQuery;
}

export interface IChangedFieldsDefinition {
  [key: string]: string;
}

export interface IActionStatusItem {
  id: string;
  failedAction: string;
  action: IAction | string;
  status: ACTION_STATUS;
  actionPayload: any;
}

export interface IPendingInformation {
  isPending: boolean;
}

export enum ScheduleMode {
  now = 'now',
  schedule = 'schedule',
}

export interface IScheduleData {
  mode: ScheduleMode;
  value: string;
}

export type FieldsToKeepAfterUpdateType = {
  [key in FieldsToKeepInDocumentAfterUpdateEnum]?: string;
};

export interface IKeyValueAny {
  [key: string]: any;
}

export interface IBaseEntityResponse<T = IBaseEntityType>
  extends IApiListResponse<T> {
  errors?: ServerError[];
}

export interface ServerError {
  id?: string;
  title?: string;
  detail?: string;
  stack?: string;
  status?: number;
}

export interface IEntityValidateRequest<T = Record<string, Primitive>> {
  _id: EntityId[];
  _change: T;
}

export interface IEntityValidationItem {
  _id: EntityId;
  allowed: boolean;
}

export type EntityValidateResponse = IEntityValidationItem[];

export interface IBulkUpdateDocumentsResult<T = IBaseEntityType> {
  totalCount: number;
  successCount: number;

  created_documents?: T[];
  updated_documents?: T[];
}

export interface IMergeBulkUpdateDocumentsResult {
  bulkUpdateResult: IBulkUpdateDocumentsResult;
  needDmSyncItemCount: number;
  updateItemCount: number;
}

export interface IRequestOptions {
  filter?: string;
  headers?: DocumentOperationHeaders;
}

export interface TooltipIconConfig {
  icon?: 'icon-info' | 'icon-warning' | string;
  tooltip?: string;
  class?: string;
  containerClass?: string;
  tooltipConfig?: ITooltipConfig;
}

export interface ITooltipConfig {
  tooltip?: string;
  containerClass?: string;
  placement?: string;
  container?: string | false;
  template?: string;
}

export interface IBeforePageLoadPopupOptions {
  onCancel?: () => void;
}

export interface IInputPreferenceDecimalPrecision {
  overrideMax: boolean;
  overrideMin: boolean;
}

export interface IChangeStatusReq {
  transfer_ids: string[];
  to_status: string;
}

export interface IChangeStatusRes {
  total: number;
  list: IBaseEntityType[];
}

export interface IChangeStatusTotalRes {
  successful: IChangeStatusRes;
  failed: IChangeStatusRes;
}

export enum TASK_STATUS {
  pending = 'pending',
  running = 'running',
  failed = 'failed',
  completed = 'completed',
}

export interface ITask {
  company_id: string;
  created_at: string;
  status: TASK_STATUS;
  sub_tasks: ISubTask[];
  type: string;
  updated_at: string;
  _id: string;
  error_message?: string;
}

export interface ISubTask {
  _id: string;
  company_id: string;
  type: string;
  status: TASK_STATUS;
  error_message: string;
  value: {
    transfer_id: string;
    is_acknowledge?: boolean;
  };
}

export enum TASK_TYPE {
  UpdateTransfer = 'transfer-bulk-change-status/update-transfer',
}

export interface ITaskOptions {
  isSync?: boolean;
  noLoader?: boolean;
  currentStep?: number;
  stepCount?: number;
}

export interface ICaseInsensitiveUniqueValidatorCustomOptions {
  currentEntityId?: string;
  customErrorMessage?: string;
  include_active?: boolean;
}

export type ICaseInsensitiveUniqueValidatorFn = (
  fieldName?: string,
  customValidOptions?: ICaseInsensitiveUniqueValidatorCustomOptions,
) => AsyncValidatorFn;

export interface IBackToFrontItemMapperOptions
  extends IDocumentOperationOptions {
  skipTranslationMapper?: boolean;
  entityType?: string;
}

export interface IFormListControlMapperOptions {
  type?: string;
  controlName?: string;
  partialUpdate?: boolean | { identifyPath: string };
  markDirty?: boolean;
}

export interface IUpdateResCompleted {
  type: 'completed';
  data: IBaseEntityType[];
}

export type ErrorType = XVIError[] | XVIError;
export interface ErrorsObject {
  errors: ErrorType;
}

export interface IUpdateResFailed {
  type: 'failed';
  data: ErrorsObject | ErrorsObject[];
}

export interface IUpdateResIntermediate {
  type: 'intermediate'; // all dependent bulk updates before the last one(with result)
  cartId: string;
  cartEntityId: string;
}

export interface IUpdateResSkipped {
  type: 'skipped'; // handler added to addDeps, but it didn't call dependent bulk update
}

export interface IUpdateResAddedToPackage {
  type: 'addedToPackage';
}

export type IUpdateResult =
  | IUpdateResCompleted
  | IUpdateResIntermediate
  | IUpdateResSkipped
  | IUpdateResFailed
  | IUpdateResAddedToPackage;

export type IUpdateDocumentsResult =
  | IBaseEntityType
  | IBaseEntityType[]
  | ErrorsObject
  | ErrorsObject[];

export type IUpdateResultExpanded = IUpdateResult | IUpdateDocumentsResult;

export interface IDependentRequestParamBase {
  options?: IDocumentOperationOptions & {
    /**
     * If true, the response will be mapped using the service's response mapper after fetching the data.
     */
    responseMapper?: (
      res: IApiListResponse<IBaseEntityType>,
    ) => IApiListResponse<IBaseEntityType>;
  };
}

export interface IDependentRequestPlainParam
  extends IDependentRequestParamBase {
  entityType: ENTITY_TYPES;
  query?: IFilterQuery;
}

/**
 * Custom dependent request.
 *
 * This is used by list/form-list containers to fetch non-entity data (or data
 * that doesn't have a dedicated ENTITY_TYPES + service mapping) alongside the
 * main entity list.
 *
 * NOTE: This is intentionally additive to keep backward compatibility.
 */
export interface ICustomDependentRequestParam
  extends IDependentRequestParamBase {
  /**
   * entityKey to store the response in dependentEntitiesData.
   * Example: 'usersInfo'
   */
  entityKey: string;

  /**
   * Factory returning the request observable.
   *
   * Should NOT throw; errors will be caught by the container and mapped to null
   * (same behavior as entity dependent requests).
   */
  request$: (params: {
    injector: Injector;
  }) => Observable<Partial<IApiListResponse<IBaseEntityType>>>;
}

export type DependentRequestParam =
  | ENTITY_TYPES
  | IDependentRequestPlainParam
  | ICustomDependentRequestParam;
